import { AlertTriangle, ChevronRight, Lightbulb, Target } from 'lucide-react';
import type { OverallSummary } from './types';

interface ExecutiveSummaryPanelProps {
  summary: OverallSummary;
}

export function ExecutiveSummaryPanel({ summary }: ExecutiveSummaryPanelProps) {
  const hasHighlights = (summary.key_highlights?.length ?? 0) > 0;
  const hasRecommendations = (summary.recommendations?.length ?? 0) > 0;
  const hasRisks = (summary.risk_factors?.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      {/* Narrative summary */}
      {summary.executive_summary && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <Lightbulb className="h-4 w-4 text-primary-600" aria-hidden="true" />
            </div>
            <h3 className="text-sm font-semibold text-neutral-900">Executive Summary</h3>
          </div>
          <p className="text-sm text-neutral-700 leading-relaxed">{summary.executive_summary}</p>
        </div>
      )}

      {/* Three-column grid: highlights / recommendations / risks */}
      {(hasHighlights || hasRecommendations || hasRisks) && (
        <div className="grid gap-4 md:grid-cols-3">
          {hasHighlights && (
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-3">
                Key Highlights
              </h3>
              <ul className="space-y-2" role="list">
                {summary.key_highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                    <ChevronRight
                      className="h-3.5 w-3.5 text-primary-500 mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasRecommendations && (
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
                <Target className="h-3 w-3" aria-hidden="true" />
                Recommendations
              </h3>
              <ul className="space-y-2" role="list">
                {summary.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                    <ChevronRight
                      className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasRisks && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-red-600 mb-3">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                Risk Factors
              </h3>
              <ul className="space-y-2" role="list">
                {summary.risk_factors.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0"
                      aria-hidden="true"
                    />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
